import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { KgdMe, KgdOverview, KgdRow } from '../../api/types';
import { CosmeticAvatar } from '../cosmetics/CosmeticAvatar';
import { track } from '../../analytics';
import { haptics } from '../../telegram';

/** «1 642» — моноширинные цифры, разряды через пробел, без десятичных. */
export function kgdNum(n: number): string {
  return new Intl.NumberFormat('ru-RU').format(Math.round(n));
}

/** Иконка «i» из хэндоффа (круг r=9 + штрих). */
function InfoI({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5M12 7.8h0.01" />
    </svg>
  );
}

/** Крупные/средние слоты зачёта: очки в золоте, ноль занят, пустой — пунктир. */
export function KgdSlots({
  results,
  bestCount,
  size = 'big',
}: {
  results: number[];
  bestCount: number;
  size?: 'big' | 'mid';
}) {
  const cells = Array.from({ length: bestCount }, (_, i) => (i < results.length ? results[i] : null));
  return (
    <div className={`kq-slots kq-slots--${size}`}>
      {cells.map((v, i) =>
        v === null ? (
          <span key={i} className="kq-slot kq-slot--empty">·</span>
        ) : v === 0 && size === 'mid' ? (
          <span key={i} className="kq-slot kq-slot--zero mono">0</span>
        ) : (
          <span key={i} className="kq-slot kq-slot--fill mono">{v}</span>
        ),
      )}
    </div>
  );
}

/** Мини-слоты в строке таблицы: только заполненность. */
function MiniSlots({ filled, bestCount }: { filled: number; bestCount: number }) {
  return (
    <span className="kq-mini">
      {Array.from({ length: bestCount }, (_, i) => (
        <i key={i} className={i < filled ? 'on' : ''} />
      ))}
    </span>
  );
}

/** Строка статуса шапки — все варианты README §3. */
function statusLine(me: KgdMe, finished: boolean): JSX.Element | null {
  if (finished && !(me.hasPlayed && me.inZone)) return null;
  if (!me.hasPlayed) {
    return (
      <>Ещё не играл <span>— любая игра добавит очки</span></>
    );
  }
  if (me.inZone) {
    if (finished || me.leadOver5th === null || me.leadOver5th === undefined) return <>Едешь</>;
    return (
      <>Едешь <span>— отрыв от 5-го {kgdNum(me.leadOver5th)}</span></>
    );
  }
  if (me.gapToTop4 === null || me.gapToTop4 === undefined) return null;
  const full = (me.slotsFilled ?? 0) >= me.bestCount;
  return (
    <>
      До четвёрки {kgdNum(me.gapToTop4)}{' '}
      <span>
        {full && me.neededPoints !== null && me.neededPoints !== undefined
          ? `— нужен результат от ${kgdNum(me.neededPoints)}`
          : '— любая игра добавит очки'}
      </span>
    </>
  );
}

/** Шапка «Моя позиция» (закреплённая; тап → экран 2). */
export function KgdMyHeader({
  me,
  gamesTotal,
  finished,
  onTap,
}: {
  me: KgdMe;
  gamesTotal: number;
  finished: boolean;
  onTap?: () => void;
}) {
  const results = me.hasPlayed ? (me.bestResults ?? []) : [];
  const filled = me.slotsFilled ?? 0;
  const free = me.bestCount - filled;
  const line = statusLine(me, finished);
  return (
    <div className="kq-myhead" onClick={onTap} role={onTap ? 'button' : undefined}>
      <div className="kq-nums">
        <div>
          <div className="kq-label mono">Лучшие {me.bestCount} из {gamesTotal} игр</div>
          <div className="kq-big mono">{me.hasPlayed ? kgdNum(me.total ?? 0) : '0'}</div>
        </div>
        <div className="kq-nums-right">
          <div className="kq-label mono">Место</div>
          <div className="kq-big mono">{me.hasPlayed ? me.position : '—'}</div>
        </div>
      </div>
      <KgdSlots results={results} bestCount={me.bestCount} size="big" />
      <div className="kq-undersl">
        <span>
          {filled >= me.bestCount
            ? `В зачёте ${me.bestCount} лучших результатов — следующая игра заменит худший`
            : filled > 0
              ? `В зачёте ${filled} результатов, свободно ${free}`
              : 'Зачёт пуст — всё впереди'}
        </span>
        <span className="mono kq-played">{me.hasPlayed ? `${me.gamesPlayed} сыграно` : ''}</span>
      </div>
      {line && <div className="kq-status">{line}</div>}
    </div>
  );
}

/** Строка игрока (карточка четвёрки и остальной список). */
function Row({ r, bestCount, onMe }: { r: KgdRow; bestCount: number; onMe?: () => void }) {
  return (
    <div
      className={`kq-row${r.isMe ? ' kq-row--me' : ''}`}
      onClick={r.isMe && onMe ? onMe : undefined}
      role={r.isMe && onMe ? 'button' : undefined}
    >
      <span className="kq-pos mono">{r.position}</span>
      <CosmeticAvatar name={r.name} src={r.avatarUrl} size={28} frame={r.cosmetics?.frame} />
      <span className="kq-mid">
        <span className="kq-nick">
          {r.name}
          {r.isReplacement && <i className="kq-repl">замена</i>}
        </span>
        <MiniSlots filled={r.slotsFilled} bestCount={bestCount} />
      </span>
      <span className="kq-right">
        <span className="kq-total mono">{kgdNum(r.total)}</span>
        {r.delta24h !== null ? (
          <span className="kq-delta mono">+{kgdNum(r.delta24h)}</span>
        ) : (
          <span className="kq-sub mono">{r.slotsFilled} из {bestCount}</span>
        )}
      </span>
    </div>
  );
}

/** Bottom sheet «Моя позиция» (8b) — по тапу на свою строку. */
function MySheet({
  me,
  gamesTotal,
  tiebreakWon,
  nextId,
  onClose,
}: {
  me: KgdMe;
  gamesTotal: number;
  tiebreakWon: boolean;
  nextId: number | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const free = me.bestCount - (me.slotsFilled ?? 0);
  const full = free <= 0;
  return (
    <div className="kq-dim" onClick={onClose}>
      <div className="kq-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="kq-handle" />
        <div className="kq-nums">
          <div>
            <div className="kq-label mono">Лучшие {me.bestCount} из {gamesTotal} игр</div>
            <div className="kq-big mono">{kgdNum(me.total ?? 0)}</div>
          </div>
          <div className="kq-nums-right">
            <div className="kq-label mono">Место</div>
            <div className="kq-big mono">{me.position}</div>
          </div>
        </div>
        <div className="kq-dist">
          {me.inZone
            ? me.leadOver5th !== null && me.leadOver5th !== undefined
              ? `Отрыв от 5-го — ${kgdNum(me.leadOver5th)} очков`
              : 'Едешь'
            : me.gapToTop4 !== null && me.gapToTop4 !== undefined
              ? `До 4-го места — ${kgdNum(me.gapToTop4)} очков`
              : ''}
        </div>
        {tiebreakWon && <div className="kq-tiebreak">выше по тай-брейку</div>}
        <KgdSlots results={me.bestResults ?? []} bestCount={me.bestCount} size="mid" />
        <div className="kq-card">
          {full
            ? me.neededPoints !== null && me.neededPoints !== undefined
              ? <>Чтобы обойти 4-е место, нужен результат <b>от {kgdNum(me.neededPoints)} очков</b>{me.neededPlace ? ` — это ${me.neededPlace.place}-е место при ${me.neededPlace.field} игроках` : ''}</>
              : <>Зачёт полный — следующая игра заменит худший результат.</>
            : <>Любая игра добавит очки — {free === 1 ? 'свободен 1 слот' : `свободно ${free} ${free < 5 ? 'слота' : 'слотов'}`}.</>}
        </div>
        {nextId !== null && (
          <button
            className="kq-cta"
            onClick={() => {
              track('kgd_register_tap', { from: 'sheet' });
              navigate(`/tournaments/${nextId}`);
            }}
          >
            Записаться на игру
          </button>
        )}
        <Link className="kq-more" to="/qualification/kaliningrad/me" onClick={onClose}>
          Подробнее о зачёте
        </Link>
      </div>
    </div>
  );
}

/** Экран 1 — лидерборд Калининграда (7b). Сегмент рисует родитель. */
export function KaliningradBoard({ data }: { data: KgdOverview }) {
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const event = data.event;
  const me = data.me;
  if (!event || !me) return null;
  const finished = data.status === 'finished';
  const rows = data.rows ?? [];
  const zone = rows.filter((r) => r.inZone);
  const rest = rows.filter((r) => !r.inZone);
  const myRow = rows.find((r) => r.isMe) ?? null;
  const nextId = data.nextTournament?.id ?? null;
  const endsLabel = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
    .format(new Date(event.endsAt))
    .replace(/\s\d{4}.*$/, '');
  const openSheet = () => {
    haptics.light();
    track('kgd_sheet_open', {});
    setSheetOpen(true);
  };

  return (
    <div className="kq-board">
      <div className="kq-context">
        <span>{event.tourLabel} · до {endsLabel}</span>
        <button className="kq-info" onClick={() => navigate('/qualification/kaliningrad/rules')} aria-label="Как это работает">
          <InfoI />
        </button>
      </div>

      <KgdMyHeader
        me={me}
        gamesTotal={event.gamesTotal}
        finished={finished}
        onTap={() => navigate('/qualification/kaliningrad/me')}
      />

      {rows.length === 0 ? (
        <div className="kq-empty">
          <p>
            {event.gamesTotal} игр, в зачёт идут {event.bestCount} лучших. Четверо лучших едут в {event.city}.
            Каждая игра до {event.bestCount}-й добавляет очки напрямую — плохой вечер ничего не отнимает.
          </p>
          {nextId !== null && (
            <button className="kq-cta" onClick={() => navigate(`/tournaments/${nextId}`)}>
              Записаться на игру
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="kq-tablehead">
            <span className="ttl">Едут в {event.city}</span>
            <span className="cnt mono">{event.seats} из {event.playersCount}</span>
          </div>
          <div className="kq-explain">
            Сумма {event.bestCount} лучших игр из {event.gamesTotal} — плохой вечер ничего не отнимает
          </div>

          <div className="kq-zone">
            {zone.map((r) => (
              <Row key={r.playerId} r={r} bestCount={event.bestCount} onMe={openSheet} />
            ))}
          </div>
          <div className="kq-rest">
            {rest.map((r) => (
              <Row key={r.playerId} r={r} bestCount={event.bestCount} onMe={openSheet} />
            ))}
          </div>
          <div className="kq-footer">В зачёт идут {event.bestCount} лучших игр из {event.gamesTotal}</div>
        </>
      )}

      {sheetOpen && me.hasPlayed && (
        <MySheet
          me={me}
          gamesTotal={event.gamesTotal}
          tiebreakWon={Boolean(myRow?.tiebreakWon)}
          nextId={nextId}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}

/** Подпись сегмента Калининграда по статусу. */
export function kgdSegmentLabel(data: KgdOverview | undefined): string {
  if (!data || data.status === 'none') return '';
  if (data.status === 'upcoming') return 'скоро';
  if (data.status === 'finished') return 'Отбор завершён';
  const e = data.event;
  if (!e) return '';
  if (data.status === 'counting') return `игра ${Math.min(e.gamesPlayed + 1, e.gamesTotal)} · подсчёт`;
  return `${e.gamesPlayed} из ${e.gamesTotal} игр`;
}
